/** The Readiness screen's pure arithmetic and decisions, kept out of the component so each can be
 * tested without a DOM.
 *
 * **Everything here is display arithmetic.** A delta, a month's share or a count of days is a
 * statement about how two figures the server already produced compare; it is never the basis of a
 * filed figure (`sumForDisplay`'s own rule). Nothing here reads a document, a factor or a ledger.
 *
 * Two rules recur and are the reason this file exists rather than inline expressions:
 *
 * - **Absent is not zero.** A delta against a period this workspace does not hold, or whose read
 *   did not answer, is `undefined`. Rendering it as `0.0%` would say "nothing moved", which is a
 *   claim, where the truth is that nothing was compared.
 * - **A fall is not good news.** Direction is `up`, `down` or `flat`, and nothing here says
 *   `better` or `worse`: a smaller inventory can be a divestment (ACC-10 separates the two), and a
 *   larger one can be a site that was missing last year.
 */

import { differenceOf, sumDecimals } from "../lib/decimal";
import { shortDate } from "../lib/format";

const DAY = 86_400_000;

/** A calendar date as a day number, without a time zone: `YYYY-MM-DD` read as UTC midnight. */
export function dayNumber(iso: string): number | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return undefined;
  return Math.round(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY);
}

/** Today's calendar date in the reader's own zone, as `YYYY-MM-DD`.
 *
 * Local parts rather than `toISOString()`: at 23:00 in California the UTC date is already
 * tomorrow, and "30 September has passed" must not become true an evening early. */
export function isoToday(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Whole days from `from` to `to`; negative when `to` is earlier. `undefined` for a non-date. */
export function daysBetween(from: string, to: string): number | undefined {
  const a = dayNumber(from);
  const b = dayNumber(to);
  return a === undefined || b === undefined ? undefined : b - a;
}

/** `25 days ago`, `in 16 days`, `today`: how far a date is from today, in words.
 *
 * Words rather than a signed number, because "-25" next to a date reads as an error code. */
export function relativeDays(days: number): string {
  if (days === 0) return "today";
  const n = Math.abs(days);
  const unit = n === 1 ? "day" : "days";
  return days > 0 ? `in ${n} ${unit}` : `${n} ${unit} ago`;
}

interface Dated {
  period_start: string;
  period_end: string;
  label?: string;
}

/** The reporting window that ends the day before this one starts, if the workspace holds one.
 *
 * **Held, not inferred.** The prior period is one of the workspace's own declared windows or it
 * does not exist: "the twelve months before this range" is a window the product has no figures
 * for under any filing, and a comparison against it would be a comparison with something nobody
 * declared. So a custom range, or a first year, has no prior and every delta against it is absent.
 */
export function priorWindowOf<T extends Dated>(
  current: Dated | undefined,
  windows: readonly T[],
): T | undefined {
  if (!current) return undefined;
  const start = dayNumber(current.period_start);
  if (start === undefined) return undefined;
  return windows.find((w) => dayNumber(w.period_end) === start - 1);
}

/** `FY2024` from `Meridian Pacific, Inc. · FY2024`: the part of a window's label that names the
 * period. Falls back to the dates when the label has no such part. */
export function periodName(window: Dated): string {
  const tail = window.label?.split(" · ").pop();
  return tail && tail.length > 0 ? tail : `${window.period_start} to ${window.period_end}`;
}

/** A day number back to `YYYY-MM-DD`. */
function isoOf(day: number): string {
  return new Date(day * DAY).toISOString().slice(0, 10);
}

const lastDayOfMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/** The stretch of equal length immediately before a window, **and only a window of whole months**.
 *
 * For a workspace that has activity before its first declared reporting period (the seeded one has
 * three years and declares one), the prior period is not a row anywhere but is perfectly
 * well-defined: the same number of months, ending the day before this one starts. It is used only
 * when no declared window fits (`priorWindowOf` is asked first), only for a window that is whole
 * calendar months (anything else has no unambiguous "same length before"), and the screen then
 * **names it by its dates and never as a financial year**: "2024" for a calendar year, "the previous
 * period" otherwise. A delta against it is still absent when that period holds no figures. */
export function previousWindow(
  current: { period_start: string; period_end: string } | undefined,
): { period_start: string; period_end: string; label: string } | undefined {
  if (!current) return undefined;
  const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(current.period_start);
  const b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(current.period_end);
  if (!a || !b) return undefined;
  const [y0, m0, d0] = [Number(a[1]), Number(a[2]), Number(a[3])];
  const [y1, m1, d1] = [Number(b[1]), Number(b[2]), Number(b[3])];
  const months = (y1 - y0) * 12 + (m1 - m0) + 1;
  if (d0 !== 1 || d1 !== lastDayOfMonth(y1, m1) || months < 1 || months > 36) return undefined;
  const start = Date.UTC(y0, m0 - 1 - months, 1) / DAY;
  const priorStart = isoOf(start);
  const priorEnd = isoOf(Date.UTC(y0, m0 - 1, 1) / DAY - 1);
  const calendarYear = months === 12 && m0 === 1;
  return {
    period_start: priorStart,
    period_end: priorEnd,
    label: calendarYear ? String(y0 - 1) : "the previous period",
  };
}

// ---------------------------------------------------------------------------------- deltas

export type Direction = "up" | "down" | "flat";

/** A change between two periods, as the card prints it: an arrow and a magnitude. */
export interface Change {
  direction: Direction;
  /** `8.6%` or `12 pts`: the magnitude only, no sign. The arrow carries the direction. */
  magnitude: string;
}

/** The change in a total, as a percentage of the prior one.
 *
 * `undefined` when the prior is not a positive finite number: a percentage of zero is not a
 * number, and "up infinity per cent" over a period with no inventory is nonsense that happens to
 * be arithmetic. A change that rounds to 0.0% is **flat**, which is an answer, and distinct from
 * absent, which is none. */
export function percentChange(current: number, prior: number): Change | undefined {
  if (!Number.isFinite(current) || !Number.isFinite(prior) || prior <= 0) return undefined;
  const pct = ((current - prior) / prior) * 100;
  const rounded = Math.abs(pct).toFixed(1);
  if (rounded === "0.0") return { direction: "flat", magnitude: "0.0%" };
  return { direction: pct > 0 ? "up" : "down", magnitude: `${rounded}%` };
}

/** The change in a share, in percentage points, between the two **rounded** shares the cards print.
 *
 * Between the rounded values rather than the exact ones, so "94% (up 3 pts)" always agrees with the
 * prior card's "91%": a delta that disagrees with the numbers beside it by one is how a reader
 * learns to distrust the lot. */
export function pointsChange(current: number, prior: number): Change | undefined {
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return undefined;
  const diff = Math.round(current) - Math.round(prior);
  if (diff === 0) return { direction: "flat", magnitude: "0 pts" };
  return { direction: diff > 0 ? "up" : "down", magnitude: `${Math.abs(diff)} pts` };
}

// ---------------------------------------------------------------------------------- the chain

export interface Chain {
  total: number;
  complete: number;
  /** Checked, and a link does not hold. */
  broken: number;
  /** Not checked for a list this long: neither whole nor broken. */
  unchecked: number;
}

/** The three states of the evidence chain, from the **server's** summary of the whole window.
 *
 * `GET /inventory?evidence=true` runs the six-link assessment over every figure the window holds and
 * reports how many are whole, how many are untraceable, and - since the home screen stopped loading
 * the list - how many of the untraceable ones were *not looked at* (a list past the store-check
 * ceiling). That last count is what keeps this three states and not two: painting 3,458 figures
 * "broken" because their stored files were not checked would make the strictest claim this product
 * can make from the one thing it had not examined.
 *
 * Nothing here assesses anything. Whole is `figures - untraceable`; broken is the untraceable ones
 * that were checked. A summary that contradicts itself (more unchecked than untraceable) is clamped
 * rather than shown as a negative count.
 */
export function chainOfEvidence(summary: {
  figures: number;
  untraceable: number;
  unchecked?: number;
}): Chain {
  const untraceable = Math.min(Math.max(summary.untraceable, 0), summary.figures);
  const unchecked = Math.min(Math.max(summary.unchecked ?? 0, 0), untraceable);
  return {
    total: summary.figures,
    complete: summary.figures - untraceable,
    unchecked,
    broken: untraceable - unchecked,
  };
}

// ---------------------------------------------------------------------------------- by month

/** One calendar month of a window, as the server's monthly series states it (ADR-0110). */
export interface MonthRow {
  /** `YYYY-MM`. */
  key: string;
  /** Kilograms, allocated: a record that spans months holds a calendar-day share in each. */
  s1: number;
  s2: number;
  /** The month's Scope 1 plus Scope 2 as an exact decimal string (the bars' height and the label
   * above it), added in decimals and never in floats; `s1` and `s2` are for geometry. */
  total: string;
  /** How many figures meet this month, and how many of those are grade A. */
  n: number;
  gradeA: number;
}

/** The server's monthly series as chart rows. Capped at 36 months so a mistyped range cannot draw a
 * decade (the server's own limit is 60). Display arithmetic only: the kilograms are the series'. */
export function monthRows(series: {
  months: readonly {
    month: string;
    figure_count: number;
    graded_a_figure_count: number;
    by_scope: readonly { scope: string; kgco2e: string }[];
  }[];
}): MonthRow[] {
  const text = (m: (typeof series.months)[number], scope: string) =>
    m.by_scope.find((cell) => cell.scope === scope)?.kgco2e ?? "0";
  return series.months.slice(0, 36).map((m) => ({
    key: m.month,
    s1: Number(text(m, "1")),
    s2: Number(text(m, "2")),
    total: sumDecimals([text(m, "1"), text(m, "2")]) ?? "0",
    n: m.figure_count,
    gradeA: m.graded_a_figure_count,
  }));
}

/** The part of a calendar month that falls inside a window: the dates a month's bar was drawn
 * over, and so the window to ask `/figures` about to list the figures that bar counted. `undefined`
 * for a key that is not `YYYY-MM`, a window with no such month, or no window. */
export function monthBucket(
  window: { period_start: string; period_end: string } | undefined,
  key: string,
): { period_start: string; period_end: string } | undefined {
  const match = /^(\d{4})-(\d{2})$/.exec(key);
  if (!window || !match) return undefined;
  const [year, month] = [Number(match[1]), Number(match[2])];
  if (month < 1 || month > 12) return undefined;
  const first = `${match[1]}-${match[2]}-01`;
  const last = `${match[1]}-${match[2]}-${String(lastDayOfMonth(year, month)).padStart(2, "0")}`;
  const start = first > window.period_start ? first : window.period_start;
  const end = last < window.period_end ? last : window.period_end;
  return start <= end ? { period_start: start, period_end: end } : undefined;
}

// ---------------------------------------------------------------------------------- the verdict

/** Where a filing stands on the ladder `Verdict` defines, as a position and a word.
 *
 * `step` is how many of the three rungs (checks passed, approved, issued) hold: it is the verdict
 * restated, never a score. A report that is `incomplete` is at step 0 however many rules passed,
 * because `Verdict`'s own rule is that every not-ready branch returns first. */
export interface Stage {
  step: 0 | 1 | 2 | 3;
  word: string;
  /** The checks ran and passed: the only thing the ring counts. */
  passed: boolean;
}

export function stageOfVerdict(verdict: string): Stage {
  switch (verdict) {
    case "issued":
      return { step: 3, word: "Issued", passed: true };
    case "approved":
      return { step: 2, word: "Approved", passed: true };
    case "checks_passed":
      return { step: 1, word: "Checks passed", passed: true };
    case "incomplete":
      return { step: 0, word: "Checks not passed", passed: false };
    case "unsupported":
      return { step: 0, word: "Not supported", passed: false };
    default:
      return { step: 0, word: "Not evaluated", passed: false };
  }
}

/** The same text with every spaced dash a regulation's own label carries written as a hyphen.
 *
 * The regime labels come from `regimes/definitions.py` with em-dashes in them and changing one is
 * a `RegimeSpec.version` bump (CLAUDE.md), so the product's no-em-dash rule is kept at the point
 * of display. This changes punctuation and nothing else: the words are still the row's own. */
export function plain(text: string): string {
  return text.replace(/\s+[—–]\s+/g, " - ").replace(/[—–]/g, "-");
}

/** What a filing is called on this screen: its regime, as `UK SECR`. The key is the server's and is
 * the same string every drill link and the table fallback use, so a filing has one name here. */
export function filingName(o: { regime_key: string }): string {
  return o.regime_key.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------------- breakdowns

/** A site as the inventory states it: the server's name, id and window-allocated total. */
export interface SiteInput {
  facility_id: string;
  facility_name: string;
  kgco2e: string;
}

/** How many sites are drawn by name before the rest are gathered into one part. */
export const SITE_LIMIT = 6;

/** The sites of a workspace as a ranked list with the tail gathered into one part (ADR-0102,
 * rule 6).
 *
 * **`other` is the exact sum of the sites not drawn**, so the parts equal the whole by
 * construction and a reader is never left with a remainder that is nobody's. A tail of one is not
 * gathered: "other (1 site)" is a site with its name taken off. Ordering is the server's (largest
 * first, ties by name) restated, so a split read from a stale sort cannot call a small site large.
 *
 * `unreadable` means a total was not a decimal: nothing is drawn rather than a sum that skipped it.
 */
export type SiteSplit =
  | {
      state: "ok";
      rows: SiteInput[];
      other?: { ids: string[]; count: number; kgco2e: string };
    }
  | { state: "unreadable" };

export function splitSites(sites: readonly SiteInput[], limit: number): SiteSplit {
  const ranked = [...sites].sort(
    (a, b) => Number(b.kgco2e) - Number(a.kgco2e) || a.facility_name.localeCompare(b.facility_name),
  );
  if (ranked.some((site) => sumDecimals([site.kgco2e]) === undefined)) return { state: "unreadable" };
  if (ranked.length <= limit + 1) return { state: "ok", rows: ranked };
  const tail = ranked.slice(limit);
  const kgco2e = sumDecimals(tail.map((site) => site.kgco2e));
  if (kgco2e === undefined) return { state: "unreadable" };
  return {
    state: "ok",
    rows: ranked.slice(0, limit),
    other: { ids: tail.map((site) => site.facility_id), count: tail.length, kgco2e },
  };
}

/** Whether a set of parts adds up to the whole, exactly (ADR-0102, rule 5).
 *
 * **Three answers, not two.** `matches`, `differs` (with the difference, signed: parts minus
 * whole) and `unreadable` (something was not a decimal, so nothing can be said). A chart that
 * collapsed the last into the first would be claiming a reconciliation nobody performed. */
export type Reconciliation =
  | { state: "matches"; whole: string }
  | { state: "differs"; parts: string; whole: string; difference: string }
  | { state: "unreadable" };

export function reconcile(parts: readonly string[], whole: readonly string[]): Reconciliation {
  const partsTotal = sumDecimals(parts);
  const wholeTotal = sumDecimals(whole);
  if (partsTotal === undefined || wholeTotal === undefined) return { state: "unreadable" };
  const difference = differenceOf(partsTotal, wholeTotal);
  if (difference === undefined) return { state: "unreadable" };
  return /^-?0*(?:\.0*)?$/.test(difference)
    ? { state: "matches", whole: wholeTotal }
    : { state: "differs", parts: partsTotal, whole: wholeTotal, difference };
}

/** One period on the trend. `computed` is a window nobody declared (see `previousWindow`). */
export interface ChainWindow {
  period_start: string;
  period_end: string;
  /** What to call it: a declared period's own name, a calendar year, or its dates. */
  name: string;
  computed: boolean;
}

/** The entity a window's label names, so a trend does not step from one legal entity's year into
 * another's: `Meridian Pacific, Inc. · FY2024` -> `Meridian Pacific, Inc.` */
const entityOf = (label: string | undefined): string | undefined => {
  const parts = label?.split(" · ");
  return parts && parts.length > 1 ? parts[0] : undefined;
};

const datesOf = (window: { period_start: string; period_end: string }): string =>
  `${shortDate(window.period_start)} to ${shortDate(window.period_end)}`;

/** The periods leading up to (and including) the one in force, **oldest first**, at most `max`.
 *
 * Each step back is a **declared** window ending the day before this one starts if the workspace
 * has one - preferring the same legal entity, because two entities' years are not one series - and
 * otherwise the equal stretch of whole months before it (`previousWindow`). A computed window is
 * named by a calendar year or by its dates and **never as a financial year nobody declared**
 * (ADR-0102, rule 8). The chain stops when neither exists, so a window that is not whole months
 * has no history rather than an invented one. */
export function windowChain(
  current: (Dated & { label?: string }) | undefined,
  windows: readonly (Dated & { label?: string })[],
  max = 4,
): ChainWindow[] {
  if (!current) return [];
  const own = periodName(current);
  const chain: ChainWindow[] = [
    {
      period_start: current.period_start,
      period_end: current.period_end,
      name: own === "custom range" || own.includes(" to ") ? datesOf(current) : own,
      computed: false,
    },
  ];
  let cursor: Dated & { label?: string } = current;
  while (chain.length < max) {
    const start = dayNumber(cursor.period_start);
    if (start === undefined) break;
    const declared = windows.filter((w) => dayNumber(w.period_end) === start - 1);
    const pick = declared.find((w) => entityOf(w.label) === entityOf(cursor.label)) ?? declared[0];
    if (pick) {
      chain.unshift({
        period_start: pick.period_start,
        period_end: pick.period_end,
        name: periodName(pick),
        computed: false,
      });
      cursor = pick;
      continue;
    }
    const computed = previousWindow(cursor);
    if (!computed) break;
    chain.unshift({
      period_start: computed.period_start,
      period_end: computed.period_end,
      name: /^\d{4}$/.test(computed.label) ? computed.label : datesOf(computed),
      computed: true,
    });
    cursor = computed;
  }
  return chain;
}
